import { cloneElement, isValidElement, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { CaretDown, CaretRight } from '@phosphor-icons/react'
import { NAV_RAIL_COLLAPSE_MS } from './layoutConstants'

// Helper function to check if a URL matches the current pathname
// Supports both exact match and prefix match (for parent routes)
const isUrlActive = (url, pathname, { exact = false } = {}) => {
  if (!url || !pathname) return false

  // Normalize both URL and pathname by removing trailing slashes for comparison
  const normalizedUrl = url.endsWith('/') ? url.slice(0, -1) : url
  const normalizedPathname = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname

  // Exact match
  if (normalizedUrl === normalizedPathname) return true

  // Avoid matching every route when url normalizes to "" (e.g. "/")
  if (!normalizedUrl || exact) return false

  // Prefix match: pathname starts with url followed by /
  // E.g., url="/productos" matches pathname="/productos/123/detalles"
  if (normalizedPathname.startsWith(normalizedUrl + '/')) return true

  return false
}

// Helper function to recursively check if any child is active
const hasActiveChild = (children, pathname) => {
  if (!children) return false

  const childArray = Array.isArray(children) ? children : [children]

  return childArray.some((child) => {
    if (!child || !child.props) return false

    // Check if this child's URL matches the current pathname
    if (isUrlActive(child.props.url, pathname)) return true

    // Check if any of the child's activeURLs match the current pathname
    if (child.props.activeURLs && child.props.activeURLs.length > 0) {
      if (child.props.activeURLs.some((activeUrl) => isUrlActive(activeUrl, pathname))) {
        return true
      }
    }

    // Check if this child was explicitly marked as active
    if (child.props.isActive) return true

    // Recursively check nested children
    return hasActiveChild(child.props.children, pathname)
  })
}

const FLYOUT_CLOSE_DELAY_MS = 160

const NavItem = ({
  collapsed,
  itemName,
  url,
  canBeSeen,
  level,
  icon,
  isActive,
  activeURLs,
  exact = false,
  children
}) => {
  const [showChildren, setShowChildren] = useState(false)
  const [flyoutReady, setFlyoutReady] = useState(() => Boolean(collapsed))
  const [isAccordionClosing, setIsAccordionClosing] = useState(false)
  const [submenuTop, setSubmenuTop] = useState(0)
  const currentPathname = useLocation().pathname
  const navigate = useNavigate()

  const parentItemRef = useRef(null)
  const closeTimerRef = useRef(null)
  const accordionCloseTimerRef = useRef(null)
  const prevCollapsedRef = useRef(collapsed)

  // Check if any of the activeURLs match the current pathname
  const isAnyActiveUrl =
    activeURLs && activeURLs.length > 0
      ? activeURLs.some((activeUrl) => isUrlActive(activeUrl, currentPathname))
      : false

  // Only the current page item gets the strong active style.
  // Parents with an active descendant stay open with a softer style.
  const isSelfActive =
    Boolean(isActive) || isAnyActiveUrl || isUrlActive(url, currentPathname, { exact })
  const hasActiveDescendant = hasActiveChild(children, currentPathname)

  const itemStateClass = [
    isSelfActive ? 'active-navbar' : '',
    // Parents with an active descendant stay highlighted (expanded or collapsed rail).
    // Leaf items like Inicio must not keep a highlight after navigating away.
    !isSelfActive && children && (showChildren || hasActiveDescendant) ? 'nav-item--open' : ''
  ]
    .filter(Boolean)
    .join(' ')

  const renderedIcon = isValidElement(icon)
    ? cloneElement(icon, { weight: itemStateClass ? 'bold' : (icon.props.weight || 'regular') })
    : icon

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }

  const handleShowChildrenChange = (e) => {
    e.preventDefault()
    if (url && !children) {
      navigate(url)
      return
    }
    if (!collapsed) {
      if (showChildren) {
        setShowChildren(false)
        setIsAccordionClosing(true)
        window.clearTimeout(accordionCloseTimerRef.current)
        accordionCloseTimerRef.current = window.setTimeout(() => {
          setIsAccordionClosing(false)
          accordionCloseTimerRef.current = null
        }, NAV_RAIL_COLLAPSE_MS)
      } else {
        window.clearTimeout(accordionCloseTimerRef.current)
        accordionCloseTimerRef.current = null
        setIsAccordionClosing(false)
        setShowChildren(true)
      }
    }
  }

  const handleMouseEnter = () => {
    if (collapsed && children) {
      clearCloseTimer()
      // Calculate position for fixed positioning
      if (level === 0 && parentItemRef.current) {
        const rect = parentItemRef.current.getBoundingClientRect()
        setSubmenuTop(rect.top)
      }
      setShowChildren(true)
    }
  }

  const handleMouseLeave = () => {
    if (collapsed && children) {
      clearCloseTimer()
      // Delay close so the cursor can cross the rail → flyout bridge
      closeTimerRef.current = setTimeout(() => {
        setShowChildren(false)
        closeTimerRef.current = null
      }, FLYOUT_CLOSE_DELAY_MS)
    }
  }

  // Keep open sections mounted while the rail width animates, then switch to flyouts.
  useEffect(() => {
    const becameCollapsed = collapsed && !prevCollapsedRef.current
    prevCollapsedRef.current = collapsed

    if (!collapsed) {
      setFlyoutReady(false)
      if (isSelfActive || hasActiveDescendant) {
        setShowChildren(true)
      }
      return undefined
    }

    if (!becameCollapsed) {
      return undefined
    }

    setIsAccordionClosing(false)
    clearCloseTimer()
    const timer = window.setTimeout(() => {
      setFlyoutReady(true)
      setShowChildren(false)
    }, NAV_RAIL_COLLAPSE_MS)

    return () => {
      window.clearTimeout(timer)
      clearCloseTimer()
    }
  }, [collapsed, isSelfActive, hasActiveDescendant])

  useEffect(
    () => () => {
      clearCloseTimer()
      window.clearTimeout(accordionCloseTimerRef.current)
    },
    []
  )

  const chevron = (open, sideways = false) =>
    sideways ? (
      <CaretRight
        className={`nav-bar-show-icon ${open ? 'is-open' : ''}`}
        size={18}
        weight="bold"
        aria-hidden="true"
      />
    ) : (
      <CaretDown
        className={`nav-bar-show-icon ${open ? 'is-open' : ''}`}
        size={18}
        weight="bold"
        aria-hidden="true"
      />
    )

  const hasChildrenHint = children ? (
    <span
      className="nav-has-children-hint"
      aria-hidden="true"
      title={`Más opciones de ${itemName}`}
    >
      <CaretRight className="nav-has-children-hint__icon" size={11} weight="bold" />
    </span>
  ) : null

  const label = (text, { visuallyHidden = false } = {}) => (
    <span className="nav-bar-item-label" aria-hidden={visuallyHidden || undefined}>
      {text}
    </span>
  )
  const isVisible = canBeSeen !== false
  const collapsedFlyout = Boolean(collapsed && flyoutReady)
  const showRailChildren = Boolean(
    children && (showChildren || isAccordionClosing) && !collapsedFlyout
  )
  const railChildren = showRailChildren ? (
    <div className={`nav-children ${showChildren && !collapsed ? 'is-open' : ''}`}>
      <div className="nav-children-inner">{children}</div>
    </div>
  ) : null

  return (
    <>
      {/* ///// Nivel 0 de items de Navbar ////// */}
      {level === 0 && isVisible && (
        <div
          className={collapsedFlyout && children ? 'nav-flyout-anchor' : undefined}
          onMouseEnter={collapsedFlyout && children ? handleMouseEnter : undefined}
          onMouseLeave={collapsedFlyout && children ? handleMouseLeave : undefined}
        >
          <div
            ref={parentItemRef}
            onClick={(e) => handleShowChildrenChange(e)}
            className={`${itemStateClass} nav-bar-level-cero cursor-pointer ${
              collapsedFlyout && children ? 'nav-bar-level-cero--has-children' : ''
            }`}
            title={collapsedFlyout && children ? `${itemName} (más opciones)` : itemName}
            aria-haspopup={children ? 'menu' : undefined}
          >
            {renderedIcon}
            {label(itemName, { visuallyHidden: collapsed })}
            {children && chevron(showChildren)}
            {collapsedFlyout && hasChildrenHint}
          </div>
          {railChildren}
          {children && showChildren && collapsedFlyout && (
            <div className="navbar-level-zero-collapsed" style={{ top: `${submenuTop}px` }}>
              <div className="navbar-flyout-title">{itemName}</div>
              {children}
            </div>
          )}
        </div>
      )}

      {/* ///// Nivel 1 de items de Navbar ////// */}
      {level === 1 &&
        canBeSeen &&
        (children ? (
          <>
            {collapsedFlyout ? (
              <div
                className="nav-flyout-anchor nav-flyout-anchor--nested"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <div
                  onClick={(e) => handleShowChildrenChange(e)}
                  className={`${itemStateClass} nav-bar-level-one cursor-pointer`}
                  title={`${itemName} (más opciones)`}
                  aria-haspopup="menu"
                >
                  {renderedIcon}
                  {label(itemName)}
                  {chevron(false, true)}
                </div>

                {showChildren && (
                  <div className="navbar-level-two-collapsed" aria-label={itemName}>
                    {children}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div
                  onClick={(e) => handleShowChildrenChange(e)}
                  className={`${itemStateClass} nav-bar-level-one cursor-pointer`}
                  title={itemName}
                >
                  {renderedIcon}
                  {label(itemName)}
                  {chevron(showChildren)}
                </div>

                {railChildren}
              </>
            )}
          </>
        ) : (
          <div
            onClick={(e) => {
              e.preventDefault()
              if (url) navigate(url)
            }}
            className={`${isSelfActive ? 'active-navbar' : ''} nav-bar-level-one cursor-pointer`}
            title={itemName}
          >
            {renderedIcon}
            {label(itemName)}
          </div>
        ))}

      {/* ///// Nivel 2 de items de Navbar ////// */}
      {level === 2 && canBeSeen && (
        <div
          onClick={(e) => {
            e.preventDefault()
            if (url) navigate(url)
          }}
          className={`${isSelfActive ? 'active-navbar' : ''} nav-bar-level-two cursor-pointer`}
          title={itemName}
        >
          {label(itemName)}
        </div>
      )}
    </>
  )
}
export default NavItem
